import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDeliverPatient } from '../../../../hooks/useDeliverPatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import { deliverAligners, deliverRefinementAligners } from '../../../../services/patient/delivery';
import { fetchSdiVersions } from '../../../../services/patient/sdi';
import { fetchRdiVersions } from '../../../../services/patient/rdi';
import { updateScanId } from '../../../../services/patient';
import { MonitoringService } from '../../../../services/patient/monitoring';
import { PatientHeaderCard } from '../PatientApprove/PatientHeaderCard';
import { DeliverAlignersSection } from './DeliverAlignersSection';
import { DeliverFooter } from './DeliverFooter';
import { PatientActionPage } from '../shared/PatientActionPage';
import { VersionedPdfPane } from '../shared/VersionedPdfPane';
import type { PatientFile } from '../../../../services/cloud-files';

type UploadMode = 'files' | 'scanId';

interface PatientDeliverProps {
    /** 'initial' = Init-13 deliver (SDI); 'refinement' = Ref-9 deliver (RDI). */
    variant?: 'initial' | 'refinement';
}

const PatientDeliver: React.FC<PatientDeliverProps> = ({ variant = 'initial' }) => {
    const navigate = useNavigate();
    const { data: patient, isLoading } = useDeliverPatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const isRefinement = variant === 'refinement';
    const docLabel = isRefinement ? 'RDI' : 'SDI';

    const [chartNotes, setChartNotes] = useState('');
    const [ack, setAck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);

    // Starter scan state
    const [scanMode, setScanMode] = useState<UploadMode>('files');
    const [scanId, setScanId] = useState('');
    const [upperFiles, setUpperFiles] = useState<PatientFile[]>([]);
    const [lowerFiles, setLowerFiles] = useState<PatientFile[]>([]);
    const [biteFiles, setBiteFiles] = useState<PatientFile[]>([]);

    const isUploading = uploadingCount > 0;
    const handleUploadStateChange = (_isUploading: boolean, delta: 1 | -1): void => {
        setUploadingCount((prev) => Math.max(0, prev + delta));
    };

    const patientId = patient?.id ?? null;
    const patientName = useMemo(() => patient?.name ?? '', [patient]);
    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;

    const isStarter = !isRefinement && patient?.alignerOption === 'Starter';

    const filesReady = upperFiles.length > 0 && lowerFiles.length > 0 && biteFiles.length > 0;
    const starterRecordsReady = scanMode === 'scanId' ? scanId.trim().length > 0 : filesReady;

    const canDeliver = ack && !submitting && !isUploading && (!isStarter || starterRecordsReady);

    const { data: instructionsVersions } = useQuery({
        queryKey: ['patient', patientId, isRefinement ? 'rdi-versions' : 'sdi-versions'],
        queryFn: () => (isRefinement ? fetchRdiVersions(patientId!) : fetchSdiVersions(patientId!)),
        enabled: !!patientId,
        staleTime: 60_000
    });

    const sendActivationLinkMutation = useMutation({
        mutationFn: () => MonitoringService.sendDMActivationLink(patientId!),
        onSuccess: (result) => {
            if (result.success) {
                showToast('Activation link sent to patient', 'success');
                return;
            }
            showToast(result.error ?? 'Failed to send activation link', 'error');
        },
        onError: () => {
            showToast('Failed to send activation link', 'error');
        }
    });

    const handleDeliver = async (): Promise<void> => {
        if (!patientId || !canDeliver) return;
        setSubmitting(true);
        try {
            if (isStarter && scanMode === 'scanId') {
                try {
                    await updateScanId(patientId, scanId.trim());
                } catch (err) {
                    const message = err instanceof Error ? err.message : 'Failed to save scan ID';
                    showToast(message, 'error');
                    return;
                }
            }

            const deliver = isRefinement ? deliverRefinementAligners : deliverAligners;
            await deliver(patientId, { chartNotes: chartNotes.trim(), acknowledged: true });
            markActionCompleted(
                patientId,
                isRefinement ? 'Deliver refinement aligners' : 'Deliver aligners'
            );
            showToast(
                isRefinement
                    ? 'Refinement aligners marked as delivered'
                    : 'Aligners marked as delivered',
                'success'
            );
            navigate('/home');
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to mark aligners as delivered';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle={isRefinement ? 'Deliver refinement aligners' : 'Deliver aligners'}
            tabIdPrefix="deliver"
            leftPane={
                <VersionedPdfPane
                    patientId={patientId}
                    versions={instructionsVersions ?? []}
                    docLabel={docLabel}
                />
            }
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        dmAppActivationStatus={patient?.dmAppActivationStatus ?? null}
                        onSendActivationLink={
                            patientId ? () => sendActivationLinkMutation.mutate() : undefined
                        }
                        isSendingActivationLink={sendActivationLinkMutation.isPending}
                        compact
                    />
                    <DeliverAlignersSection
                        patientId={patientId}
                        chartNotes={chartNotes}
                        onChartNotes={setChartNotes}
                        ack={ack}
                        onAckChange={setAck}
                        docLabel={docLabel}
                        isStarter={isStarter}
                        scanMode={scanMode}
                        onScanModeChange={setScanMode}
                        scanId={scanId}
                        onScanIdChange={setScanId}
                        onUpperFilesChange={setUpperFiles}
                        onLowerFilesChange={setLowerFiles}
                        onBiteFilesChange={setBiteFiles}
                        onUploadStateChange={handleUploadStateChange}
                    />
                </>
            }
            footer={
                <DeliverFooter
                    canDeliver={canDeliver}
                    isSubmitting={submitting}
                    onDeliver={() => void handleDeliver()}
                />
            }
        />
    );
};

export default PatientDeliver;
